import { MenuItem, Select, SelectChangeEvent, Card, CardContent, Chip, Tooltip } from "@mui/material";
import React, { useContext, useMemo } from "react";
import { AdminProjectContext } from "../../contexts/AdminProjectContext";
import { TrendingUp, People, AttachMoney, Assessment, Warning, Add, FolderSpecial } from "@mui/icons-material";
import { SELECTED_PROJECT_STORAGE_KEY, ADMIN_VIEWS, PROJECT_STATUS_LABELS, PROJECT_STATUS_COLORS } from "../../utils/constant";
import DashboardSkeleton from "../ui/DashboardSkeleton";
import { AdminActiveProjectDetailsView } from "./overview/AdminActiveProjectDetailsView";
import { AdminActiveProjectMonthlyDataView } from "./overview/AdminActiveProjectMonthlyDataView";
import AdminActiveProjectUsersView from "./overview/AdminActiveProjectUsersView";
import "../../styles/AdminDashboardView.css";

interface AdminDashboardViewProps {
    selectedMenu: (view: number) => void;
}

export function AdminDashboardView(props: AdminDashboardViewProps) {
    const {
        projects,
        selectedProject,
        selectProjectWithParticipants,
        reloadDashboard,
        setReloadDashboard,
        loading,
        error
    } = useContext(AdminProjectContext);

    const handleOnProjectSelection = async (e: SelectChangeEvent<unknown>) => {
        const selectedProjectId = e.target.value?.toString();
        const project = projects?.find(p => p.getProjectId() === selectedProjectId);
        if (project) {
            // Charger les participants pour le projet sélectionné (lazy loading)
            await selectProjectWithParticipants(project);
            const projectId = project.getProjectId();
            if (projectId) {
                localStorage.setItem(SELECTED_PROJECT_STORAGE_KEY, projectId);
            }
        }
    };




    const projectMetrics = useMemo(() => {
        if (!selectedProject) return null;

        const penaltyAmount = selectedProject.penaltyAmountPerDay;
        const currency = selectedProject.currency || '';
        const formatValue = (val: number) => val.toLocaleString('fr-FR');

        const activeParticipants = selectedProject.Users?.length || 0;
        const totalParts = selectedProject.Users?.reduce((sum, user) => {
            return sum + (user.UserProject?.share || 0);
        }, 0) || 0;

        const totalAmount = selectedProject.contribution * totalParts;
        const contribution = selectedProject.contribution;
        const cashFund = selectedProject.cashFundAmount || 0;

        return [
            {
                label: "Participants actifs",
                value: activeParticipants.toString(),
                icon: <People />,
                gradient: "linear-gradient(45deg, #667eea, #764ba2)",
                tooltip: "Membres inscrits au projet"
            },
            {
                label: "Pénalité de retard",
                value: penaltyAmount ? `${formatValue(penaltyAmount)} ${currency}` : "-",
                icon: <Warning />,
                gradient: "linear-gradient(45deg, #f44336, #ff9800)",
                tooltip: "Montant facturé par jour de retard"
            },
            {
                label: "Montant total",
                value: `${formatValue(totalAmount)} ${currency}`,
                icon: <AttachMoney />,
                gradient: "linear-gradient(45deg, #f093fb, #f5576c)",
                tooltip: "Cotisation × Nombre de parts"
            },
            {
                label: "Cotisation",
                value: `${formatValue(contribution)} ${currency}`,
                icon: <TrendingUp />,
                gradient: "linear-gradient(45deg, #4facfe, #00f2fe)",
                tooltip: "Montant mensuel par part"
            },
            {
                label: "Nombre total de parts",
                value: totalParts.toString(),
                icon: <Assessment />,
                gradient: "linear-gradient(45deg, #ffb347, #ffcc33)",
                tooltip: "Somme des parts de tous les participants"
            },
            {
                label: "Fond de caisse",
                value: `${formatValue(cashFund)} ${currency}`,
                icon: <Assessment />,
                gradient: "linear-gradient(45deg, #43e97b, #38f9d7)",
                tooltip: "Réserve pour imprévus"
            }
        ];
    }, [selectedProject]);

    if (!loading && (!projects || projects.length === 0)) {
        return (
            <div className="dashboard-container">
                <Card className="empty-state-card">
                    <CardContent className="empty-state-content">
                        <div className="empty-state-icon">
                            <FolderSpecial />
                        </div>
                        <h2 className="empty-state-title">Aucun projet disponible</h2>
                        <p className="empty-state-description">
                            Vous n'avez pas encore créé de projet. Commencez par créer votre premier projet
                            pour commencer à gérer vos participants et leurs contributions.
                        </p>
                        <div className="empty-state-actions">
                            <button
                                className="empty-state-primary-button"
                                onClick={() => props.selectedMenu(ADMIN_VIEWS.NEW_PROJECT_VIEW)}
                            >
                                <Add /> Créer mon premier projet
                            </button>
                            <button
                                className="empty-state-secondary-button"
                                onClick={() => props.selectedMenu(ADMIN_VIEWS.PROJECT_LIST_VIEW)}
                            >
                                <FolderSpecial /> Voir tous les projets
                            </button>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="dashboard-container">
                <DashboardSkeleton />
            </div>
        );
    }

    return (
        <div className="dashboard-container">
            {error && (
                <div className="admin-error-container">
                    <h3>Erreur</h3>
                    <p>{error}</p>
                    <button className="admin-retry-button" onClick={() => setReloadDashboard(!reloadDashboard)}>
                        Réessayer
                    </button>
                </div>
            )}

            {projects && (
                <div className="project-selector">
                    <div className="selector-label">
                        <TrendingUp sx={{ color: "var(--primary-color)" }} />
                        Projet sélectionné
                    </div>
                    <Select
                        value={selectedProject?.getProjectId() || ""}
                        onChange={handleOnProjectSelection}
                        fullWidth
                        className="styled-select"
                    >
                        {projects?.map((project) => (
                            <MenuItem key={project.getProjectId()} value={project.getProjectId()} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                                <span>{project.name}</span>
                                <Chip
                                    label={PROJECT_STATUS_LABELS[project.status as keyof typeof PROJECT_STATUS_LABELS] || project.status}
                                    size="small"
                                    sx={{
                                        backgroundColor: `${PROJECT_STATUS_COLORS[project.status as keyof typeof PROJECT_STATUS_COLORS]}20`,
                                        color: PROJECT_STATUS_COLORS[project.status as keyof typeof PROJECT_STATUS_COLORS],
                                        fontWeight: 600,
                                        fontSize: '0.75rem',
                                        marginLeft: '1rem'
                                    }}
                                />
                            </MenuItem>
                        ))}
                    </Select>
                </div>
            )}

            {/* Two Column Layout */}
            <div className="dashboard-columns">
                {/* Colonne Gauche: Métriques + Planning */}
                <div className="dashboard-column">
                    {projectMetrics && (
                        <div className="metrics-section">
                            <div className="section-header">
                                <div className="section-icon">
                                    <Assessment />
                                </div>
                                <h2 className="section-title">Aperçu du projet</h2>
                            </div>
                            <div className="metrics-grid">
                                {projectMetrics.map((metric, index) => (
                                    <Tooltip key={index} title={metric.tooltip} placement="top">
                                        <div className="metric-card">
                                            <div className="metric-content">
                                                <div className="metric-header">
                                                    <div className="metric-info">
                                                        <div className="metric-label">{metric.label}</div>
                                                        <div className="metric-value">{metric.value}</div>
                                                    </div>
                                                    <div className="metric-icon-container">
                                                        {metric.icon}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </Tooltip>
                                ))}
                            </div>
                        </div>
                    )}

                    {selectedProject && <AdminActiveProjectDetailsView />}
                </div>

                {/* Colonne Droite: Données mensuelles + Participants */}
                <div className="dashboard-column">
                    {selectedProject && <AdminActiveProjectMonthlyDataView />}
                    {selectedProject && <AdminActiveProjectUsersView selectedMenu={props.selectedMenu} />}
                </div>
            </div>
        </div>
    );
}
